// Results Component Structure Template:
/*
Results Section Structure:

1. Understanding Your Results Block:
   - Title with Brain icon
   - Variant: "highlight"
   - Chart showing user's ratings comparison
   - Explanation of personal results
   
2. Original Study Results Block:
   - Title with Microscope icon
   - Variant: "info"
   - Chart showing original study data
   - Explanation of study findings

3. About the Study Block:
   - Title with BookOpen icon
   - Variant: "muted"
   - Study citation and brief description
   - Educational disclaimer

Chart Data Structure:
{
  name: string;
  rating: number;
  fill: string;
}

Chart Configuration:
- ResponsiveContainer (width: 100%, height: 160)
- BarChart (layout: vertical)
- XAxis (type: number, domain: [0, 5])
- YAxis (type: category)
- Mobile responsiveness handling
- Custom bar labels
*/

import React, { useState, useEffect, useRef } from 'react';
import { SimulationTemplate, SimulationStep, ResultBlock } from '../SimulationTemplate';
import { Text } from '../../../components/ui/typography/Text';
import { AccentButton } from '../../../components/ui/buttons/AccentButton';
import { OutlineButton } from '../../../components/ui/buttons/OutlineButton';
import { Brain, Microscope, BookOpen, Snowflake } from 'lucide-react';

const scenarios = [
  {
    id: 1,
    title: "Scenario A: The $10 Movie Ticket",
    description: "You purchased a $10 ticket to see a movie you've been looking forward to.",
    weatherDesc: "On the day of the movie, a severe snowstorm hits your area. The roads are slippery, visibility is poor, and the temperature is well below freezing.",
    cost: 10
  },
  {
    id: 2,
    title: "Scenario B: The $50 Movie Ticket",
    description: "You purchased a $50 ticket to see the exact same movie you've been looking forward to.",
    weatherDesc: "On the day of the movie, an identical severe snowstorm hits your area. The roads are just as slippery, visibility is equally poor, and the temperature is the same.",
    cost: 50
  }
];

interface ScenarioState {
  stage: 'intro' | 'weather' | 'feedback';
  choice: boolean | null;
  showWeatherAnimation: boolean;
  progress: number;
}

const SunkCostFallacySimulation: React.FC = () => {
  const [started, setStarted] = useState(false);
  const [currentScenario, setCurrentScenario] = useState(1);
  const [scenarioStates, setScenarioStates] = useState<Record<number, ScenarioState>>({
    1: {
      stage: 'intro',
      choice: null,
      showWeatherAnimation: false,
      progress: 0
    },
    2: {
      stage: 'intro',
      choice: null,
      showWeatherAnimation: false,
      progress: 0
    }
  });
  
  const simulationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scenario = scenarioStates[currentScenario];
    if (scenario.showWeatherAnimation) {
      const timer = setInterval(() => {
        setScenarioStates(prev => ({
          ...prev,
          [currentScenario]: {
            ...prev[currentScenario],
            progress: Math.min(prev[currentScenario].progress + 2, 100)
          }
        }));
      }, 50);
      return () => clearInterval(timer);
    }
  }, [scenarioStates[currentScenario].showWeatherAnimation, currentScenario]);

  const startScenario = (scenarioId: number) => {
    if (scenarioStates[scenarioId].choice !== null) return;

    setScenarioStates(prev => ({
      ...prev,
      [scenarioId]: {
        ...prev[scenarioId],
        stage: 'weather',
        showWeatherAnimation: true
      }
    }));
  };

  const handleChoice = (scenarioId: number, wouldGo: boolean) => {
    setScenarioStates(prev => ({
      ...prev,
      [scenarioId]: {
        ...prev[scenarioId],
        choice: wouldGo,
        stage: 'feedback'
      }
    }));
  };

  const handleReset = () => {
    setStarted(false);
    setCurrentScenario(1);
    setScenarioStates({
      1: {
        stage: 'intro',
        choice: null,
        showWeatherAnimation: false,
        progress: 0
      },
      2: {
        stage: 'intro',
        choice: null,
        showWeatherAnimation: false,
        progress: 0
      }
    });
    
    simulationRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const nextScenario = () => {
    setCurrentScenario(2);
  };

  const simulationContent = (
    <div ref={simulationRef} className="space-y-8">
      {/* Scenario A */}
      <SimulationStep 
        title={scenarios[0].title}
        isActive={currentScenario === 1 && scenarioStates[1].stage === 'intro'}
      >
        <div className="space-y-6">
          <Text className="text-gray-700">
            {scenarios[0].description}
          </Text>
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-1">
              <span className="text-5xl font-semibold text-green-500">
                ${scenarios[0].cost}
              </span>
            </div>
          </div>
          {scenarioStates[1].stage === 'intro' && (
            <div className="flex justify-center mt-4">
              <AccentButton onClick={() => startScenario(1)}>
                Start Scenario
              </AccentButton>
            </div>
          )}
        </div>
      </SimulationStep>

      {/* Weather Stage - Scenario A */}
      {scenarioStates[1].stage !== 'intro' && (
        <SimulationStep 
          title="Weather Conditions"
          isActive={currentScenario === 1 && scenarioStates[1].stage === 'weather'}
        >
          <div className="space-y-6">
            <Text className="text-gray-700">
              {scenarios[0].weatherDesc}
            </Text>
            <div className="space-y-4">
              <div className="relative py-4">
                <div className="w-full bg-gradient-to-r from-blue-50 to-blue-100 rounded-full h-3 relative overflow-hidden">
                  <div 
                    className="absolute inset-0 bg-stripes opacity-20"
                    style={{
                      backgroundSize: '20px 20px',
                      backgroundImage: 'linear-gradient(45deg, rgba(59, 130, 246, 0.5) 25%, transparent 25%, transparent 50%, rgba(59, 130, 246, 0.5) 50%, rgba(59, 130, 246, 0.5) 75%, transparent 75%, transparent)',
                      animation: 'move-stripes 1s linear infinite'
                    }}
                  />
                  <div 
                    className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all duration-300 relative"
                    style={{ width: `${scenarioStates[1].progress}%` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shine" />
                  </div>
                </div>

                <div className="absolute inset-0 overflow-visible pointer-events-none" style={{ top: '1rem' }}>
                  {Array.from({ length: 10 }).map((_, i) => (
                    <Snowflake
                      key={i}
                      className={`absolute w-3 h-3 sm:w-4 sm:h-4 text-blue-500 opacity-50
                        animate-fall transform -translate-y-full
                        ${scenarioStates[1].progress > i * 10 ? 'opacity-0' : ''}`}
                      style={{
                        left: `${i * 10}%`,
                        animationDelay: `${i * 0.2}s`,
                        animationDuration: '2s',
                        transform: `rotate(${i * 36}deg) translateY(-100%)`,
                      }}
                    />
                  ))}
                </div>
              </div>

              {scenarioStates[1].progress === 100 && (
                <div className="flex flex-col sm:flex-row gap-4">
                  <OutlineButton 
                    onClick={() => handleChoice(1, true)}
                    active={scenarioStates[1].choice === true}
                    className="flex-1"
                  >
                    Go to the movie
                  </OutlineButton>
                  <OutlineButton 
                    onClick={() => handleChoice(1, false)}
                    active={scenarioStates[1].choice === false}
                    className="flex-1"
                  >
                    Stay home
                  </OutlineButton>
                </div>
              )}
            </div>
          </div>
        </SimulationStep>
      )}

      {/* Feedback - Scenario A */}
      {scenarioStates[1].stage === 'feedback' && (
        <SimulationStep 
          title="Your Decision"
          isActive={currentScenario === 1}
        >
          <div className="space-y-6">
            <div className="p-3 sm:p-4 bg-blue-50 rounded-xl border border-blue-100">
              <Text className="text-gray-700">
                You decided to {scenarioStates[1].choice ? 'brave the storm' : 'stay home'} with a ${scenarios[0].cost} ticket.
              </Text>
            </div>
            <div className="flex justify-center">
              <AccentButton onClick={nextScenario}>
                Try Next Scenario
              </AccentButton>
            </div>
          </div>
        </SimulationStep>
      )}

      {/* Scenario B */}
      {currentScenario === 2 && (
        <>
          <SimulationStep 
            title={scenarios[1].title}
            isActive={scenarioStates[2].stage === 'intro'}
          >
            <div className="space-y-6">
              <Text className="text-gray-700">
                {scenarios[1].description}
              </Text>
              <div className="flex items-center justify-center">
                <div className="flex items-center gap-1">
                  <span className="text-5xl font-semibold text-green-500">
                    ${scenarios[1].cost}
                  </span>
                </div>
              </div>
              {scenarioStates[2].stage === 'intro' && (
                <div className="flex justify-center mt-4">
                  <AccentButton onClick={() => startScenario(2)}>
                    Start Scenario
                  </AccentButton>
                </div>
              )}
            </div>
          </SimulationStep>

          {/* Weather Stage - Scenario B */}
          {scenarioStates[2].stage !== 'intro' && (
            <SimulationStep 
              title="Weather Conditions"
              isActive={scenarioStates[2].stage === 'weather'}
            >
              <div className="space-y-6">
                <Text className="text-gray-700">
                  {scenarios[1].weatherDesc}
                </Text>
                <div className="space-y-4">
                  <div className="relative py-4">
                    <div className="w-full bg-gradient-to-r from-blue-50 to-blue-100 rounded-full h-3 relative overflow-hidden">
                      <div 
                        className="absolute inset-0 bg-stripes opacity-20"
                        style={{
                          backgroundSize: '20px 20px',
                          backgroundImage: 'linear-gradient(45deg, rgba(59, 130, 246, 0.5) 25%, transparent 25%, transparent 50%, rgba(59, 130, 246, 0.5) 50%, rgba(59, 130, 246, 0.5) 75%, transparent 75%, transparent)',
                          animation: 'move-stripes 1s linear infinite'
                        }}
                      />
                      <div 
                        className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all duration-300 relative"
                        style={{ width: `${scenarioStates[2].progress}%` }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shine" />
                      </div>
                    </div>

                    <div className="absolute inset-0 overflow-visible pointer-events-none" style={{ top: '1rem' }}>
                      {Array.from({ length: 10 }).map((_, i) => (
                        <Snowflake
                          key={i}
                          className={`absolute w-3 h-3 sm:w-4 sm:h-4 text-blue-500 opacity-50
                            animate-fall transform -translate-y-full
                            ${scenarioStates[2].progress > i * 10 ? 'opacity-0' : ''}`}
                          style={{
                            left: `${i * 10}%`,
                            animationDelay: `${i * 0.2}s`,
                            animationDuration: '2s',
                            transform: `rotate(${i * 36}deg) translateY(-100%)`,
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  {scenarioStates[2].progress === 100 && (
                    <div className="flex flex-col sm:flex-row gap-4">
                      <OutlineButton 
                        onClick={() => handleChoice(2, true)}
                        active={scenarioStates[2].choice === true}
                        className="flex-1"
                      >
                        Go to the movie
                      </OutlineButton>
                      <OutlineButton 
                        onClick={() => handleChoice(2, false)}
                        active={scenarioStates[2].choice === false}
                        className="flex-1"
                      >
                        Stay home
                      </OutlineButton>
                    </div>
                  )}
                </div>
              </div>
            </SimulationStep>
          )}

          {/* Final Results */}
          {scenarioStates[2].stage === 'feedback' && (
            <>
              <SimulationStep 
                title="Your Decision"
                isActive={true}
              >
                <div className="p-3 sm:p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <Text className="text-gray-700">
                    You decided to {scenarioStates[2].choice ? 'brave the storm' : 'stay home'} with a ${scenarios[1].cost} ticket.
                  </Text>
                </div>
              </SimulationStep>

              <ResultBlock
                title="Understanding Your Results"
                icon={Brain}
                variant="highlight"
              >
                <div className="space-y-4">
                  <Text className="text-gray-700">
                    Let's analyze your decisions:
                  </Text>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>With the $10 ticket, you chose to {scenarioStates[1].choice ? 'go to the movie' : 'stay home'}</li>
                    <li>With the $50 ticket, you chose to {scenarioStates[2].choice ? 'go to the movie' : 'stay home'}</li>
                  </ul>
                  <Text className="text-gray-700">
                    {scenarioStates[1].choice === scenarioStates[2].choice ? 
                      "You made the same choice in both scenarios, suggesting you weren't influenced by the sunk cost. You focused on the current situation rather than the money already spent." :
                      "You made different choices despite identical weather conditions, suggesting the ticket price influenced your decision. This is a common example of the sunk cost fallacy."}
                  </Text>
                </div>
              </ResultBlock>

              <ResultBlock
                title="In the Original Study"
                icon={Microscope}
                variant="info"
              >
                <div className="space-y-4">
                  <Text className="text-gray-700">
                    In the 1985 study by Arkes & Blumer, participants were significantly more likely to go to the movie with a $50 ticket compared to a $10 ticket, despite identical weather conditions.
                  </Text>
                  <Text className="text-gray-700">
                    This demonstrated how people often feel compelled to follow through on an activity when they've invested more money, even when that money is already spent and can't be recovered (a "sunk cost").
                  </Text>
                </div>
              </ResultBlock>

              <ResultBlock
                title="About the Study"
                icon={BookOpen}
                variant="muted"
              >
                <div className="space-y-4">
                  <Text className="text-gray-700">
                    Based on research by Arkes & Blumer (1985), "The Psychology of Sunk Cost," published in Organizational Behavior and Human Decision Processes. The study explored how prior investments influence future decision-making, even when those costs should be irrelevant to the current choice.
                  </Text>
                  <Text className="text-sm text-gray-500 italic">
                    This simulation is designed for educational purposes. Individual results may vary.
                  </Text>
                </div>
              </ResultBlock>

              <div className="flex justify-center">
                <AccentButton onClick={handleReset}>
                  Try Again
                </AccentButton>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );

  return (
    <SimulationTemplate
      title="The Sunk Cost Fallacy"
      description="Experience how past investments can irrationally influence our decisions in this recreation of Arkes & Blumer's 1985 study. You'll face two scenarios involving movie tickets and bad weather."
    >
      {!started ? (
        <div className="flex justify-center">
          <AccentButton onClick={() => setStarted(true)}>
            Start Simulation
          </AccentButton>
        </div>
      ) : (
        simulationContent
      )}
    </SimulationTemplate>
  );
};

export default SunkCostFallacySimulation; 